import type { ReactNode } from "react"
import { Link } from "@inertiajs/react"
import { Logo } from "./Logo"
import { Flash } from "./Flash"

const trustPoints = [
  { title: "50 credits free", body: "Try it without a card. Credits never expire." },
  { title: "No emails are sent", body: "We just ask servers, politely. Your sender reputation is safe." },
  { title: "Lists deleted in 30 days", body: "Encrypted in transit and at rest. Never sold, never trained on." },
]

export default function AuthLayout({ children }: { children: ReactNode }) {
  return (
    <div className="isolate flex min-h-dvh flex-col bg-white text-ink-950 lg:grid lg:grid-cols-2">
      <Flash />

      {/* Form column */}
      <div className="flex flex-col lg:min-h-dvh">
        <header className="px-6 pt-6 lg:px-10 lg:pt-8">
          <Link href="/" aria-label="CleanMyList home" className="inline-flex items-center">
            <Logo />
          </Link>
        </header>
        <main className="relative flex flex-1 items-center justify-center px-6 py-12 lg:px-10">
          <div className="w-full max-w-sm">{children}</div>
        </main>
        <footer className="flex flex-wrap items-center justify-center gap-x-4 gap-y-1 px-6 pb-6 text-center text-xs text-ink-500 lg:justify-start lg:px-10">
          <Link href="/privacy" className="hover:text-ink-800">
            Privacy
          </Link>
          <span aria-hidden="true">·</span>
          <Link href="/terms" className="hover:text-ink-800">
            Terms
          </Link>
          <span aria-hidden="true">·</span>
          <a href="mailto:support@cleanmylist.io" className="hover:text-ink-800">
            support@cleanmylist.io
          </a>
        </footer>
      </div>

      {/* Brand panel */}
      <aside
        aria-label="Product highlights"
        className="relative hidden overflow-hidden bg-ink-950 text-white lg:flex lg:flex-col lg:justify-between lg:px-12 lg:py-12"
      >
        <div aria-hidden="true" className="dotted-grid-dark pointer-events-none absolute inset-0 opacity-60" />
        <div
          className="pointer-events-none absolute -top-32 -right-24 size-[28rem] rounded-full opacity-30 blur-3xl"
          style={{ background: "radial-gradient(circle, var(--color-brand-500), transparent 60%)" }}
        />
        <div
          className="pointer-events-none absolute -bottom-40 -left-20 size-[32rem] rounded-full opacity-25 blur-3xl"
          style={{ background: "radial-gradient(circle, var(--color-brand-400), transparent 60%)" }}
        />

        <div className="relative">
          <p className="inline-flex items-center gap-x-2 text-sm font-medium tracking-tight text-brand-300">
            <span className="size-1.5 rounded-full bg-brand-400" />
            Email verification that actually works
          </p>
          <h2 className="mt-6 max-w-[18ch] font-display text-4xl font-semibold tracking-tight text-balance text-white xl:text-5xl/[1.05]">
            Email lists that
            <span className="relative ml-2 inline-block">
              <span className="relative z-10">don't bounce.</span>
              <span
                aria-hidden="true"
                className="absolute -bottom-1 -left-1 -z-0 h-2.5 w-[105%] -rotate-1 rounded-full bg-lime-300/80"
              />
            </span>
          </h2>
          <p className="mt-5 max-w-[40ch] text-pretty text-base/7 text-ink-300">
            Clean your list before you send. Bounce rates drop, opens go up, your reputation stays clean.
          </p>
        </div>

        <figure className="relative max-w-md">
          <svg
            className="size-7 text-brand-400"
            viewBox="0 0 24 24"
            fill="currentColor"
            aria-hidden="true"
          >
            <path d="M7 5a4 4 0 0 0-4 4v8a2 2 0 0 0 2 2h4a2 2 0 0 0 2-2v-4a2 2 0 0 0-2-2H6a4 4 0 0 1 4-4V5H7zm10 0a4 4 0 0 0-4 4v8a2 2 0 0 0 2 2h4a2 2 0 0 0 2-2v-4a2 2 0 0 0-2-2h-3a4 4 0 0 1 4-4V5h-3z" />
          </svg>
          <blockquote className="mt-4 text-pretty text-lg/8 text-white">
            CleanMyList caught addresses our old process missed before the first send. The export made it obvious what
            to suppress and what to keep.
          </blockquote>
          <figcaption className="mt-5 flex items-center gap-x-3">
            <img
              src="https://assets.ui.sh/avatars/1.webp"
              alt=""
              className="size-10 rounded-full object-cover ring-2 ring-white/20"
              loading="lazy"
            />
            <div>
              <p className="text-sm font-medium tracking-tight text-white">Yara Behrens</p>
              <p className="text-xs text-ink-300">Head of Lifecycle, Bramble</p>
            </div>
          </figcaption>
        </figure>

        <ul role="list" className="relative grid grid-cols-1 gap-px overflow-hidden rounded-2xl bg-white/10">
          {trustPoints.map((tp) => (
            <li key={tp.title} className="bg-ink-950/95 px-5 py-4">
              <p className="flex items-center gap-x-2 font-display text-sm font-semibold tracking-tight text-white">
                <CheckIcon />
                {tp.title}
              </p>
              <p className="mt-1 text-xs text-ink-300">{tp.body}</p>
            </li>
          ))}
        </ul>
      </aside>
    </div>
  )
}

function CheckIcon() {
  return (
    <svg viewBox="0 0 16 16" className="size-3.5 text-brand-400" aria-hidden="true">
      <path
        d="M3 8.5l3 3 7-7.5"
        stroke="currentColor"
        strokeWidth="2"
        fill="none"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}
